import React, { useState, useCallback } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { Typography, AlertBlock, ResponsiveModal } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { useMiddleTruncate } from '../../../../hooks/useMiddleTruncate';

export interface ContentLink {
  category: string;
  url: string;
}

interface ContentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  modelName: string;
  mediaUrls: string[];
  contentLinks?: ContentLink[];
}

const ContentPreviewModal: React.FC<ContentPreviewModalProps> = ({
  isOpen,
  onClose,
  modelName,
  mediaUrls,
  contentLinks = [],
}) => {
  const { t } = useTranslations();
  const truncateMiddle = useMiddleTruncate(50);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleImageClick = (url: string) => {
    setFullscreenImage(url);
  };

  const handleCloseFullscreen = () => {
    setFullscreenImage(null);
  };

  const handleNextImage = () => {
    if (!fullscreenImage) return;
    const currentIndex = mediaUrls.indexOf(fullscreenImage);
    const nextIndex = (currentIndex + 1) % mediaUrls.length;
    setFullscreenImage(mediaUrls[nextIndex]);
  };

  const handlePrevImage = () => {
    if (!fullscreenImage) return;
    const currentIndex = mediaUrls.indexOf(fullscreenImage);
    const prevIndex = currentIndex === 0 ? mediaUrls.length - 1 : currentIndex - 1;
    setFullscreenImage(mediaUrls[prevIndex]);
  };

  const handleCopyLink = useCallback(async (url: string, index: number) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-white',
        header: 'border-b border-auth-surface',
        body: 'py-6',
        footer: 'border-t border-auth-surface',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex items-center justify-between px-4 md:px-6 py-4">
              <div className="flex items-center gap-2">
                <Typography variant="h6" className="text-auth-ink">
                  {t('partner.revshare.offers.contentPreview.title')} {modelName}
                </Typography>
              </div>
            </ModalHeader>

            <ModalBody className="px-4 md:px-6">
              {/* Info Banner */}
              {/* <div className="mb-6">
                <AlertBlock
                  data={[
                    {
                      severity: 'info',
                      title: t('partner.revshare.offers.contentPreview.authorApproved'),
                      description: t('partner.revshare.offers.contentPreview.availableAfterLaunch'),
                    },
                  ]}
                />
              </div> */}

              {/* Media Grid */}
              {mediaUrls.length > 0 && (
                <div className="flex flex-col gap-2 mb-6">
                  <Typography variant="body2" className="text-auth-ink">
                    {t('partner.revshare.accounts.contentModal.previewTitle')}
                  </Typography>
                  <div className="grid grid-cols-4 gap-4">
                    {mediaUrls.map((url, index) => (
                      <div
                        key={index}
                        className="aspect-[3/4] rounded-3 overflow-hidden bg-auth-surface cursor-pointer relative group"
                        onClick={() => handleImageClick(url)}
                      >
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                            <Icon icon="solar:eye-bold" width={24} className="text-partner" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Content Links Section */}
              {contentLinks.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Typography variant="body2" className="text-auth-ink">
                    {t('partner.revshare.accounts.contentModal.linksTitle')}
                  </Typography>
                  <div className="max-md:overflow-x-auto md:px-0 md:mx-0 -mx-4 px-4">
                    <div className="bg-white border border-tin rounded-5 overflow-hidden p-4 flex flex-col gap-2 max-md:w-[43.75rem] max-md:shrink-0">
                      {/* Table Header */}
                      <div className="flex items-center gap-6 bg-auth-surface border border-tin rounded-2.5 px-2 py-3 shadow-[0px_1px_0px_0px_rgba(0,0,0,0.05)]">
                        <div className="w-[12.5rem] pr-6">
                          <Typography variant="body2" className="font-medium text-auth-muted">
                            {t('partner.revshare.accounts.contentModal.category')}
                          </Typography>
                        </div>
                        <div className="flex-1">
                          <Typography variant="body2" className="font-medium text-auth-muted">
                            {t('partner.revshare.accounts.contentModal.contentLink')}
                          </Typography>
                        </div>
                      </div>

                      {/* Table Rows */}
                      {contentLinks.map((link, index) => (
                        <div key={index} className="flex items-center gap-6 px-2 py-3">
                          <div className="w-[12.5rem]">
                            <Typography variant="body2" className="text-auth-ink">
                              {link.category}
                            </Typography>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="bg-husky rounded-5 px-2 py-0.5 inline-flex md:flex items-center gap-1">
                              <span
                                aria-label={link.url}
                                className="text-sm text-partner truncate flex-1"
                              >
                                {truncateMiddle(link.url)}
                              </span>
                              <button
                                onClick={() => handleCopyLink(link.url, index)}
                                className="shrink-0 p-0.5 hover:opacity-70 transition-opacity"
                                title={
                                  copiedIndex === index ? t('common.copied') : t('common.copy')
                                }
                              >
                                <Icon
                                  icon={
                                    copiedIndex === index ? 'lucide:check' : 'solar:copy-linear'
                                  }
                                  width={16}
                                  className="text-partner"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {mediaUrls.length === 0 && contentLinks.length === 0 && (
                <div className="text-center py-8">
                  <Typography variant="body2" className="text-auth-muted">
                    {t('partner.revshare.offers.contentPreview.noContent') ||
                      'Контент пока недоступен'}
                  </Typography>
                </div>
              )}

              {/* Fullscreen Image Viewer */}
              {fullscreenImage && (
                <div
                  className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center"
                  onClick={handleCloseFullscreen}
                >
                  {/* Close Button */}
                  <button
                    className="absolute top-0 right-0 z-10 w-12 h-12 flex items-center justify-center"
                    onClick={handleCloseFullscreen}
                  >
                    <Icon icon="lucide:x" width={24} className="text-white" />
                  </button>

                  {/* Previous Button */}
                  {mediaUrls.length > 1 && (
                    <button
                      className="absolute left-4 z-10 w-8 h-8 flex items-center justify-center rounded-2 bg-partner shadow-[0px_10px_15px_-3px_rgba(0,111,238,0.4),0px_4px_6px_-2px_rgba(0,0,0,0.05)] hover:bg-[#005FCC] transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                    >
                      <Icon icon="solar:alt-arrow-left-linear" width={16} className="text-white" />
                    </button>
                  )}

                  {/* Next Button */}
                  {mediaUrls.length > 1 && (
                    <button
                      className="absolute right-4 z-10 w-8 h-8 flex items-center justify-center rounded-2 bg-partner shadow-[0px_10px_15px_-3px_rgba(0,111,238,0.4),0px_4px_6px_-2px_rgba(0,0,0,0.05)] hover:bg-[#005FCC] transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                    >
                      <Icon icon="solar:alt-arrow-right-linear" width={16} className="text-white" />
                    </button>
                  )}

                  {/* Image */}
                  <img
                    src={fullscreenImage}
                    alt="Fullscreen preview"
                    className="max-w-[90vw] max-h-[90vh] object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </ModalBody>

            <ModalFooter className="px-4 md:px-6 py-4">
              <Button color="primary" onPress={onClose} className="bg-partner text-white">
                {t('partner.revshare.offers.contentPreview.close')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </ResponsiveModal>
  );
};

export default ContentPreviewModal;
